import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class AssetSelectionService {
    private readonly selectedIdsSubject = new BehaviorSubject<readonly string[]>([]);
    private readonly rangeAnchorSubject = new BehaviorSubject<string | null>(null);

    public readonly selectedIds$: Observable<readonly string[]> = this.selectedIdsSubject.asObservable();
    public readonly rangeAnchor$: Observable<string | null> = this.rangeAnchorSubject.asObservable();

    public get snapshot(): string[] {
        return [...this.selectedIdsSubject.value];
    }

    public get rangeAnchor(): string | null {
        return this.rangeAnchorSubject.value;
    }

    public selectOnly(id: string): void {
        this.publish([id], id);
    }

    public toggle(id: string): void {
        const selectedIds = this.snapshot;
        const selectedIndex = selectedIds.indexOf(id);

        if (selectedIndex === -1) {
            this.publish([...selectedIds, id], id);
            return;
        }

        selectedIds.splice(selectedIndex, 1);
        const nextAnchor = this.rangeAnchor === id ? selectedIds[selectedIds.length - 1] ?? null : this.rangeAnchor;
        this.publish(selectedIds, nextAnchor);
    }

    public selectRange(orderedIds: string[], id: string): void {
        const anchorIndex = orderedIds.indexOf(this.rangeAnchor);
        const targetIndex = orderedIds.indexOf(id);

        if (anchorIndex === -1 || targetIndex === -1) {
            this.selectOnly(id);
            return;
        }

        const firstIndex = Math.min(anchorIndex, targetIndex);
        const lastIndex = Math.max(anchorIndex, targetIndex);
        this.publish(orderedIds.slice(firstIndex, lastIndex + 1), this.rangeAnchor);
    }

    public clear(): void {
        this.publish([], null);
    }

    private publish(selectedIds: string[], rangeAnchor: string | null): void {
        this.selectedIdsSubject.next(Object.freeze([...selectedIds]));
        this.rangeAnchorSubject.next(rangeAnchor);
    }
}
